import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TableComponent from "../../Global Functions/tableComponents";
import { getSiteVisitList } from "../../Redux/Actions/siteVisitActions";
import { EllipsisOutlined } from "@ant-design/icons";
import DateRangePopup from "../../Utils/dateFilterPopup";
import { Badge, Button, Dropdown, Menu } from "antd";
import { ToFirstCapital } from "../../Utils/helpers";
import { Link } from "react-router-dom";

const SiteVisitList = () => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  const { siteVisitList, loading } = useSelector((state) => state.siteVisit);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  });

  const fetchDetails = React.useCallback(
    async (page, limit, showLoader = true) => {
      const data = {
        site_visit_by: ["self", "cab"],
        page,
        limit,
        search: debouncedSearch,
        ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
        ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
      };
      try {
        await dispatch(getSiteVisitList(data, showLoader));
        if (!showLoader) {
          // toast.success("Referrals list refreshed");
        }
      } catch {
        if (!showLoader) {
          // toast.error("Failed to refresh referrals list");
        }
      }
    },
    [dispatch, debouncedSearch, dateRange]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  const handleDateRangeApply = (start, end) => {
    if (start === null && end === null) {
      setDateRange({ start: null, end: null });
    } else {
      setDateRange({ start, end });
    }
    setIsDatePopupVisible(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // useEffect(() => {
  //   console.log("Raw Site Visit List from backend:", siteVisitList);
  // }, [siteVisitList]);

  const dataSource = siteVisitList?.data?.map((item, index) => ({
    key: item._id,
    index: index + 1,
    project_id: item.project.project_id,
    project_name: item.project.project_name,
    project_address: item.project.street_address,
    no_of_guest: item.no_of_guest,
    site_visit_by: ToFirstCapital(item.site_visit_by),
    ride_type: ToFirstCapital(item.ride_type),
    pickup_address: item.pickup_address ? item.pickup_address : "N/A",
    pickup_date_time: (() => {
      const dateStr =
        item.pickup_date_time || item.site_visit_date_time || null;
      if (!dateStr) return "N/A";
      const localTimeStr = dateStr.endsWith("Z")
        ? dateStr.slice(0, -1)
        : dateStr;
      return new Date(localTimeStr).toLocaleString("en-GB", { hour12: false });
    })(),
    // pickup_date_time: (() => {
    //   const dateStr =
    //     item.pickup_date_time || item.site_visit_date_time || null;
    //   if (!dateStr) return "N/A";
    //   const dateObj = new Date(dateStr);
    //   if (isNaN(dateObj)) return "N/A";
    //   return dateObj.toLocaleString();
    // })(),
    user_name: item.user.name,
    user_email: item.user.email,
    user_contact: `${item.user.country_code} ${item.user.contact_number}`,
    site_visit_purpose: item.site_visit_purpose,
    is_round_trip: item.is_round_trip === true ? "Yes" : "No",
    cab_type: item.cab_type ? item.cab_type.name : "N/A",
    drop_address: item.drop_address,
    status: item.status,
  }));

  const columns = [
    {
      title: "Sr No.",
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Project Name",
      dataIndex: "project_name",
      key: "project_name",
      align: "center",
    },
    {
      title: "Project Address",
      dataIndex: "project_address",
      key: "project_address",
      align: "center",
      ellipsis: true,
    },
    {
      title: "User Name",
      dataIndex: "user_name",
      key: "user_name",
      align: "center",
    },
    {
      title: "Email Address",
      dataIndex: "user_email",
      key: "user_email",
      align: "center",
    },
    {
      title: "Contact Number",
      dataIndex: "user_contact",
      key: "user_contact",
      align: "center",
    },
    // {
    //   title: "No. of Guest",
    //   dataIndex: "no_of_guest",
    //   key: "no_of_guest",
    //   align: "center",
    // },

    {
      title: "Site Visit By",
      key: "site_visit_by",
      align: "center",
      dataIndex: "site_visit_by",
    },
    {
      title: "Ride Type",
      key: "ride_type",
      align: "center",
      dataIndex: "ride_type",
    },
    {
      title: "Pick Up Address",
      key: "pickup_address",
      align: "center",
      dataIndex: "pickup_address",
      ellipsis: true,
    },
    {
      title: "Pick Up Date & Time",
      key: "pickup_date_time",
      align: "center",
      dataIndex: "pickup_date_time",
    },
    {
      title: "Drop Off Address",
      key: "drop_address",
      align: "center",
      dataIndex: "drop_address",
      ellipsis: true,
    },
    {
      title: "Site Visit Purpose",
      key: "site_visit_purpose",
      align: "center",
      dataIndex: "site_visit_purpose",
    },
    {
      title: "Round Trip",
      key: "is_round_trip",
      align: "center",
      dataIndex: "is_round_trip",
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      key: "status",
      render: (status) => {
        const getStatusColor = (status) => ({
          requested: "orange",
          completed: "green",
          booked: "blue",
          cancelled: "red"
        }[status] || "default");

        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "4px 8px",
              border: "1px solid #d9d9d9",
              borderRadius: "10px",
            }}
          >
            <Badge
              color={getStatusColor(status)}
              text={ToFirstCapital(status)}
            />
          </span>
        );
      },
    },
    {
      title: "Action",
      align: "center",
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          overlay={
            <Menu>
              <Link to={`/site-visit/${record.key}`}>
                <Menu.Item key="view">View</Menu.Item>
              </Link>
            </Menu>
          }
        >
          <Button
            shape="square"
            icon={<EllipsisOutlined></EllipsisOutlined>}
          ></Button>
        </Dropdown>
      ),
    },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDetails(pagination.current, pagination.pageSize);
    }, 150);

    return () => clearTimeout(timer);
  }, [pagination.current, pagination.pageSize, fetchDetails]);


  useEffect(() => {
    if (siteVisitList?.total) {
      setPagination((prevData) => ({
        ...prevData,
        total: siteVisitList.total,
      }));
    }
  }, [siteVisitList]);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  return (
    <>
      <TableComponent
        h3heading="Site Visit List"
        pagination={pagination}
        dataSource={dataSource}
        onChange={handleTableChange}
        columns={columns}
        setState={setSearchQuery}
        onCalendarClick={() => setIsDatePopupVisible(true)}
        loading={loading}
      ></TableComponent>

      <DateRangePopup
        visible={isDatePopupVisible}
        initialStart={dateRange.start}
        initialEnd={dateRange.end}
        onCancel={() => setIsDatePopupVisible(false)}
        onOk={handleDateRangeApply}
      />
    </>
  );
};

export default SiteVisitList;
